import * as types from '../mutation-types'

const state = {
  /*
    Expects the form:
      {
        message: '', [What will be displayed]
        type: '' [danger,error,success,info]
      }
   */
  messages: [],
  modalMessages: [],
  showLogoutNotification: false,
  notification_id: 0
}

const getters = {
  // Makes the most recent notification on top
  reverseNotifications: state => {
    return state.messages.slice().reverse()
  },
  // Makes the most recent modal notification the first item
  reverseModalNotifications: state => {
    return state.modalMessages.slice().reverse()
  }
}

// If the title field of a response message contains one of these, then
// a notification will not be displayed
const NOTIFICATION_TITLE_EXCLUSION_LIST = [
  'Access denied'
]

const helpers = {
  /*
   * Parses any messages out of responses from the backend
   * into the given messages array and returns it with the new messages added
   */
  getNotificationFromResult (responseJSON) {
    // Ignore invalid json
    if (responseJSON === undefined) {
      return null;
    }

    // The response could potentially contain multiple messages
    if (Array.isArray(responseJSON)) {
      return null;
    }

    // Check if this response shouldn't be displayed
    if ('title' in responseJSON && NOTIFICATION_TITLE_EXCLUSION_LIST.includes(responseJSON['title'])) {
      return null;
    }

    // Using a custom logout notification, so don't display this one
    if ('msg' in responseJSON && responseJSON['msg'] === 'Logout complete') {
      return null;
    }

    // Get the message
    let msg = ''
    if ('msg' in responseJSON) {
      msg = responseJSON['msg']
    } else if ('detail' in responseJSON) {
      msg = responseJSON['detail']
    } else {
      return null
    }

    // Default the type to info and attempt to find the correct notification type
    let type = 'info'
    if (('error' in responseJSON && responseJSON['error']) || helpers.isErrorStatus(responseJSON['status'])) {
      type = 'danger'
    } else if ('success' in responseJSON || helpers.isSuccessStatus(responseJSON['status'])) {
      type = 'success'
    }
    return { msg, type };
  },
  pushNotifications (messages, msg, type) {
    if (typeof msg === 'string' && msg.length > 0) {
      messages.push({
        message: msg,
        type: type,
        index: state.notification_id
      })
      state.notification_id++
    }
    return messages
  },
  addUnhandledError (messages) {
    messages.push({
      type: 'error',
      message: 'An internal server error occured',
      index: state.notification_id
    })
    state.notification_id++
    return messages
  },
  /**
   * Returns if the given http status is an error state
   */
  isErrorStatus (status) {
    let topLevelNum = status / 100
    return topLevelNum === 4 || topLevelNum === 5
  },
  isSuccessStatus (status) {
    let topLevelNum = status / 100
    return topLevelNum === 2
  }
}

const mutations = {
  [types.ADD_NOTIFICATION] (state, { type, message }) {
    state.messages.push({
      type: type,
      message: message,
      timeout: false,
      delay: 0,
      index: state.notification_id
    })
    state.notification_id++
  },

  [types.ADD_NOTIFICATION_TIMEOUT] (state, { type, message, delay }) {
    state.messages.push({
      type: type,
      message: message,
      timeout: true,
      delay: delay,
      index: state.notification_id
    })
    state.notification_id++
  },

  [types.UNHANDLED_ERROR_NOTIFICATION] (state) {
    state.messages = helpers.addUnhandledError(state.messages)
  },

  [types.ACCESS_DENIED_NOTIFICATION] (state) {
    state.messages.push({
      type: 'danger',
      message: 'You do not have the necessary permissions to perform this action',
      timeout: false,
      delay: 0,
      index: state.notification_id
    })
    state.notification_id++
  },

  // Adds a notification from a returned response from the backend
  [types.ADD_NOTIFICATION_FROM_RESULT] (state, responseJSON) {
    let message = helpers.getNotificationFromResult(responseJSON)
    if (!message){
      return;
    }
    state.messages = helpers.pushNotifications(state.messages, message.msg, message.type);
    state.notification_id++
  },

  [types.HIDE_NOTIFICATION] (state, index) {
    let pos = state.messages.map(obj => obj.index).indexOf(index)
    state.messages.splice(pos, 1)
  },

  [types.HIDE_ALL_NOTIFICATIONS] (state) {
    state.messages = []
  },

  [types.ADD_MODAL_NOTIFICATION] (state, { type, message }) {
    let notification = {
      message: message,
      type: type
    }
    state.modalMessages.push(notification)
  },

  [types.ADD_MODAL_NOTIFICATION_FROM_RESULT] (state, responseJSON) {
    let message = helpers.getNotificationFromResult(responseJSON)
    if (!message){
      return;
    }
    // if the message is an error, add it to the modal message because the modal won't close. but if it is a success,
    // the modal will close making the message disappear so we add it to the regular messages
    if (message.type === 'error') {
      state.modalMessages = helpers.pushNotifications(state.modalMessages, message.msg, message.type);
    }
    else {
      state.messages = helpers.pushNotifications(state.messages, message.msg, message.type);
    }
  },

  [types.ADD_UNHANDLED_MODAL_NOTIFICATION] (state) {
    state.modalMessages = helpers.addUnhandledError(state.modalMessages)
  },

  [types.HIDE_MODAL_NOTIFICATION] (state, index) {
    state.modalMessages.splice(index, 1)
  },

  [types.HIDE_ALL_MODAL_NOTIFICATIONS] (state) {
    state.modalMessages = []
  },

  [types.SHOW_LOGOUT_NOTIFICATION] (state) {
    state.showLogoutNotification = true
  },

  [types.HIDE_LOGOUT_NOTIFICATION] (state) {
    state.showLogoutNotification = false
  }
}

const actions = {
  /*
    Add a notification that will disappear after delay milliseconds
   */
  addNotificationTimeout ({ commit }, { type, message, delay }) {
    commit(types.ADD_NOTIFICATION_TIMEOUT, {
      type: type,
      message: message,
      delay: delay
    })
    setTimeout(() => {
      commit(types.HIDE_NOTIFICATION, state.notification_id - 1)
    }, delay)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/notifications.js